"use client";
import { ArtButton, ArtDialog, ArtTable, ArtTableHandle, confirmDialog } from "@art/shared-ui";
import { HeadCell } from "@art/shared-ui";
import { SSRTableBuilder, useApiService } from "@art/shared-ui";
import { faEdit, faFolderPlus, faPlus, faTrash, faUserShield } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import ArtUserAddPage from "./add-dialog.component";
import ManageRolesDialog from "./manage-roles-dialog";
// import { confirmDialog } from "@art/shared-ui";
import { usePageWrapper } from "../../../hooks";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { AttributePermissionGate, useAttributePermission } from "../../permissions";
import "./user.css";
import { APIService } from "@art/shared-ui";
import { ART_URL } from "../../../Constant";
import ResourceAccessDialog, { AccessCell, ResourceAccess } from "./resource-access";
import { tableParamNav } from "../../../utils/table-utils";

const ArtUserSettingsPage = () => {
  const [queryParams, setQueryParams]: any = useSearchParams();

  const apiService = useApiService();
  const tableRef = useRef<ArtTableHandle>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
  const [rolesDialogUser, setRolesDialogUser] = useState<Data | null>(null);
  const [accessUser, setAccessUser] = useState<Data | null>(null);
  const [resourceAccess, setResourceAccess] = useState<Record<string, ResourceAccess>>({});
  const [resourceAccessStatus, setResourceAccessStatus] = useState<"loading" | "loaded" | "error">("loading");
  const isInvited = useSelector((state: RootState) => state.appstate.is_invited);
  const canEditUsers = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "EditUsers");
  const canDeleteUsers = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "DeleteUsers");
  const canAssignUserRoles = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "AssignUserRoles");
  const canViewAccess = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "ViewResourceAccess");
  const canManageAccess = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "ManageResourceAccess");
  const idpService = useMemo(() => new APIService({ ART_STORE: apiService.getStore(), BASE_URL: ART_URL + "/idp/api", DisableDefaultHeadder: true, DisableDefaultTenantUrl: true }), [apiService]);

  const loadResourceAccess = () => {
    setResourceAccessStatus("loading");
    return idpService.get("/tenants/current/users/resource-access").then((response: any) => {
      const items: ResourceAccess[] = response.data?.data;
      if (!Array.isArray(items)) throw new Error("Invalid resource-access response");
      setResourceAccess(Object.fromEntries(items.map((item) => [item.email.toLowerCase(), item])));
      setResourceAccessStatus("loaded");
    }).catch(() => {
      setResourceAccess({});
      setResourceAccessStatus("error");
    });
  };

  useEffect(() => {
    if (canViewAccess || canManageAccess) loadResourceAccess();
  }, [canViewAccess, canManageAccess]);


  interface Data {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    resource_access?: ResourceAccess["resource_scopes"];
  }

  const headCells: HeadCell<Data>[] = [
    {
      id: "first_name",
      numeric: false,
      disablePadding: false,
      label: "First Name",
      getColData: (row) => row.first_name,
    },
    {
      id: "last_name",
      numeric: false,
      disablePadding: false,
      label: "Last Name",
      getColData: (row) => row.last_name,
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
      getColData: (row) => row.email,
    },
    ...(canViewAccess || canManageAccess ? [{
      id: "resource_access",
      numeric: false,
      disablePadding: false,
      label: "Resource access",
      getColData: (row) => <AccessCell scopes={resourceAccess[row.email.toLowerCase()]?.resource_scopes} inheritedScopes={resourceAccess[row.email.toLowerCase()]?.inherited_resource_scopes} status={resourceAccessStatus} />,
    }] satisfies HeadCell<Data>[] : []),
  ];
  const tableBuilder = SSRTableBuilder<Data>({
    endpoint: "/user/list",
    method: "get",
    apiService: apiService,

    onFetch: (params) => {
      // return {
      //     "page": params["page"],
      //     "search_text": params["search_text"]
      // }
      let currentPage = params["page"] || "1";
      let searchText = params["search_text"] || "";

      const newParams: any = {
        page: currentPage,
      };
      if (searchText) {
        newParams.search = searchText;
      }
      setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage));
      return {
        page: currentPage,
        search_text: searchText,
      };
    },
    onResponse: (data) => {
      return {
        data: data.data.users ?? [],
        total: data.data.total_count,
      };
    },
  });

  const openAddUserDialog = (data?: Data) => {
    setSelectedData(data);
    setAddDialogOpen(true);
  };
  const closeAddUserDialog = () => {
    setSelectedData(null);
    setAddDialogOpen(false);
  };
  const onSave = () => {
    closeAddUserDialog();
    tableRef.current?.refresh(0);
  };

  const closeRolesDialog = () => setRolesDialogUser(null);
  const onRolesSaved = () => {
    apiService.showSnack("Roles updated successfully", "success");
    if (canViewAccess || canManageAccess) loadResourceAccess();
    tableRef.current?.refresh?.();
  };

  return (
    <>
      <ArtDialog
        title={
          rolesDialogUser
            ? `Manage Roles — ${rolesDialogUser.first_name} ${rolesDialogUser.last_name}`
            : ""
        }
        open={!!rolesDialogUser}
        onClose={closeRolesDialog}
      >
        {rolesDialogUser && (
          <ManageRolesDialog
            userId={rolesDialogUser.id}
            onSaved={onRolesSaved}
            closeDialog={closeRolesDialog}
          />
        )}
      </ArtDialog>

      <ResourceAccessDialog email={accessUser?.email || ""} existingScopes={accessUser ? resourceAccess[accessUser.email.toLowerCase()]?.resource_scopes : []} open={!!accessUser} onClose={() => setAccessUser(null)} onSaved={() => { loadResourceAccess(); tableRef.current?.refresh?.(); }} />

      <ArtDialog
        title={selectedData ? "Update User" : "Invite User"}
        open={addDialogOpen}
        onClose={closeAddUserDialog}
        type="form"
        actionBuilder={(props) => {
          return (
            <Box sx={{ display: "flex", gap: "15px" }}>
              <ArtButton
                buttonType="button"
                dataQa="btn-save"
                label={selectedData ? "Update" : "Invite"}
                onClick={() => props.submit?.()}
              />
              <ArtButton
                buttonType="button"
                dataQa="btn-reset"
                label="Reset"
                variant="outlined"
                color="error"
                onClick={() => props.reset?.()}
              />
            </Box>
          );
        }}
      >
        <ArtUserAddPage data={selectedData} onSave={onSave} />
        {!selectedData && (
          <Typography
            variant="subtitle2"
            className="d-flex align-items-center text-start fs-9"
            sx={{ mt: 1.5, mb: 0.5 }}
          >
            Add the user’s email address to send them an invitation. A secure
            invitation link will be sent to join ART and start collaborating
            with you in real time.
          </Typography>
        )}
      </ArtDialog>
      <Box className="non-clickable-table">
        <ArtTable
          ref={tableRef}
          dataGuide="application-users"
          headCells={headCells}
          builder={tableBuilder}
          search={{
            enabled: true,
            value: queryParams.get("search") || "",
          }}
          pagination={{
            initialPage: parseInt(queryParams.get("page") || "1") - 1,
          }}
          toolbarActions={[
            <AttributePermissionGate application="PLATFORM_SETTINGS" feature="USER_MANAGEMENT" attribute="InviteUsers">
              <ArtButton
                variant="contained"
                dataQa="btn-add"
                label="Invite user"
                icon={faPlus}
                onClick={() => {
                  openAddUserDialog();
                }}
              />
            </AttributePermissionGate>,
          ]}
          rowActions={
            !isInvited
              ? ([
                  canAssignUserRoles && {
                    icon: faUserShield,
                    label: "Manage Roles",
                    onClick: (row: Data) => {
                      setRolesDialogUser(row);
                    },
                  },
                  canEditUsers && {
                    icon: faEdit,
                    label: "Edit",
                    onClick: (row: Data) => {
                      openAddUserDialog(row);
                    },
                  },
                  canManageAccess && {
                    icon: faFolderPlus,
                    label: "Manage project access",
                    onClick: (row: Data) => setAccessUser(row),
                  },
                  canDeleteUsers && {
                    icon: faTrash,
                    label: "Remove from workspace",
                    onClick: async (row: Data) => {
                      confirmDialog("Remove user from workspace?", "This removes their access to this workspace only. Their account and access to other workspaces will remain unchanged.").then(
                        async () => {
                          try {
                            await apiService.delete(`/user/${row.id}`);
                            onSave();
                            apiService.showSnack(
                              "User removed from workspace",
                              "success"
                            );
                          } catch (err: any) {
                            apiService.showSnack(
                              err.response?.data?.message,
                              "error"
                            );
                          }
                        }
                      );
                    },
                  },
                ].filter(Boolean) as any)
              : undefined
          }
        />
      </Box>


    </>
  );
};

export default usePageWrapper(ArtUserSettingsPage, {
  page_title: "Application Users",
});
